<template>
  <div class="themed-autocomplete" :class="autocompleteClasses">
    <themed-input
      :id="id"
      :value="inputValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :error="error"
      :aria-invalid="ariaInvalid"
      :aria-describedby="ariaDescribedby"
      :aria-required="ariaRequired"
      role="combobox"
      :aria-expanded="showSuggestions ? 'true' : 'false'"
      :aria-autocomplete="'list'"
      :aria-controls="dropdownId"
      :aria-activedescendant="highlightedOptionId"
      v-bind="$attrs"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.down.prevent="navigateDown"
      @keydown.up.prevent="navigateUp"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape="closeSuggestions"
    />
    
    <!-- Loading indicator -->
    <div v-if="loading" class="themed-autocomplete__loading">
      <div class="themed-autocomplete__spinner"></div>
    </div>
    
    <!-- Suggestions dropdown -->
    <div
      v-if="showSuggestions && filteredOptions.length > 0"
      :id="dropdownId"
      class="themed-autocomplete__dropdown"
      role="listbox"
    >
      <div
        v-for="(option, index) in filteredOptions"
        :key="getOptionValue(option)"
        :id="getOptionId(index)"
        :class="[
          'themed-autocomplete__option',
          { 'themed-autocomplete__option--highlighted': index === highlightedIndex }
        ]"
        role="option"
        :aria-selected="index === highlightedIndex ? 'true' : 'false'"
        @mousedown.prevent="selectOption(option)"
        @mouseenter="highlightedIndex = index"
      >
        <slot name="option" :option="option">
          {{ getOptionLabel(option) }}
        </slot>
      </div>
    </div>
    
    <!-- No results message -->
    <div
      v-if="showSuggestions && filteredOptions.length === 0 && inputValue"
      class="themed-autocomplete__dropdown themed-autocomplete__no-results"
    >
      <slot name="noResults">
        <span>No results found</span>
      </slot>
    </div>
  </div>
</template>

<script>
import ThemedInput from '../ThemedInput.vue';

export default {
  name: 'ThemedAutocomplete',
  components: {
    ThemedInput
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: null
    },
    value: {
      type: [String, Number, Object],
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Type to search...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    minChars: {
      type: Number,
      default: 0
    },
    filterFn: {
      type: Function,
      default: null
    },
    asyncSearch: {
      type: Function,
      default: null
    },
    debounce: {
      type: Number,
      default: 300
    },
    ariaInvalid: {
      type: String,
      default: null
    },
    ariaDescribedby: {
      type: String,
      default: null
    },
    ariaRequired: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      inputValue: '',
      showSuggestions: false,
      highlightedIndex: -1,
      debounceTimer: null,
      internalLoading: false,
      uniqueId: `autocomplete-${Math.random().toString(36).substr(2, 9)}`
    };
  },
  computed: {
    autocompleteClasses() {
      return this.cn(
        'themed-autocomplete',
        this.error && 'themed-autocomplete--error',
        this.disabled && 'themed-autocomplete--disabled'
      );
    },
    filteredOptions() {
      if (!this.inputValue || this.inputValue.length < this.minChars) {
        return this.options;
      }
      
      // Use custom filter function if provided
      if (this.filterFn) {
        return this.filterFn(this.options, this.inputValue);
      }
      
      // Default filtering
      const searchTerm = this.inputValue.toLowerCase();
      return this.options.filter(option => {
        const label = this.getOptionLabel(option).toLowerCase();
        return label.includes(searchTerm);
      });
    },
    dropdownId() {
      return `${this.id || this.uniqueId}-dropdown`;
    },
    highlightedOptionId() {
      if (this.highlightedIndex >= 0) {
        return this.getOptionId(this.highlightedIndex);
      }
      return null;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (typeof newValue === 'object' && newValue !== null) {
          this.inputValue = this.getOptionLabel(newValue);
        } else {
          this.inputValue = newValue || '';
        }
      }
    }
  },
  methods: {
    getOptionLabel(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return String(option);
      }
      return option.label || option.text || option.name || String(option.value || '');
    },
    getOptionValue(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return option.value !== undefined ? option.value : option;
    },
    getOptionId(index) {
      return `${this.id || this.uniqueId}-option-${index}`;
    },
    handleInput(value) {
      this.inputValue = value;
      this.highlightedIndex = -1;
      
      // Clear previous debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // If async search is provided, debounce the search
      if (this.asyncSearch) {
        this.debounceTimer = setTimeout(() => {
          this.performAsyncSearch(value);
        }, this.debounce);
      }
      
      // Show suggestions if minimum characters met
      if (value.length >= this.minChars) {
        this.showSuggestions = true;
      } else {
        this.showSuggestions = false;
      }
      
      // Emit input event with raw value
      this.$emit('input', value);
      this.$emit('search', value);
    },
    async performAsyncSearch(query) {
      if (!this.asyncSearch) return;
      
      this.internalLoading = true;
      this.$emit('loading', true);
      
      try {
        await this.asyncSearch(query);
      } catch (error) {
        console.error('Autocomplete async search error:', error);
        this.$emit('error', error);
      } finally {
        this.internalLoading = false;
        this.$emit('loading', false);
      }
    },
    handleFocus() {
      if (this.inputValue.length >= this.minChars) {
        this.showSuggestions = true;
      }
      this.$emit('focus');
    },
    handleBlur() {
      // Delay to allow click on suggestion
      setTimeout(() => {
        this.showSuggestions = false;
        this.highlightedIndex = -1;
      }, 200);
      this.$emit('blur');
    },
    selectOption(option) {
      const value = this.getOptionValue(option);
      const label = this.getOptionLabel(option);
      
      this.inputValue = label;
      this.showSuggestions = false;
      this.highlightedIndex = -1;
      
      this.$emit('input', value);
      this.$emit('select', option);
    },
    navigateDown() {
      if (!this.showSuggestions) {
        this.showSuggestions = true;
        return;
      }
      
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++;
      }
    },
    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    },
    closeSuggestions() {
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    }
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
</script>
